import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RiAddLine, RiCheckLine } from 'react-icons/ri'
import { useUiStore } from 'store'
import { GoogleOAuthAccountLinkDTO } from '../../../../../../api-models/src'
import {
	GtmAccount,
	GtmContainer,
	GtmWorkspace
} from '../../../../../../api-models/src/advertiser/gtm'
import {
	GButton,
	GListBox,
	GTooltip,
	GTransition
} from '../../../../components/basic-blocks'
import { GoogleTagManagerAuthConfig } from '../../../../config'
import { useStores } from '../../../../hooks'
import { AiOutlineQuestionCircle } from 'react-icons/ai'
import { useTrackPostHogEvent } from '../../../../utils/posthog'
import { PostHogEvent, PostHogPath } from '../../../../utils/posthog-events'

interface ICreateTag {
	gtmAccount: GtmAccount | null
	gtmContainer: GtmContainer | null
	gtmWorkspace: GtmWorkspace | null
	oauth: 'PENDING' | 'WAITING' | 'COMPLETED'
	link: GoogleOAuthAccountLinkDTO | null
}

const getAuthCode = () => {
	return window.localStorage.getItem('google_oauth_code')
}

const clearAuthCode = () => {
	window.localStorage.removeItem('google_oauth_code')
}

interface IStateError {
	link: string | undefined
	gtmAccount: string | undefined
	gtmContainer: string | undefined
	gtmWorkspace: string | undefined
}

export const GtmAccountSelector = ({
	onComplete
}: {
	onComplete?: () => void
}) => {
	const {
		assetStore: {
			getGtmAccounts,
			asset,
			gtmTag,
			getGtmContainers,
			getGtmWorkspaces,
			demoMode,
			createGtmTag
		},
		accountStore: { createGtmAuth, account, userAccess, gtmLinks },
		userStore: { user }
	} = useStores()
	const isLoading = useUiStore((s) => s.isLoading)
	const trackPostHogEvent = useTrackPostHogEvent()

	const [gtmAccounts, setGtmAccounts] = useState<GtmAccount[]>([])
	const [gtmContainer, setContainer] = useState<GtmContainer[]>([])
	const [gtmWorkspaces, setWorkspaces] = useState<GtmWorkspace[]>([])
	const [isGTMUpdated, setIsGTMUpdated] = useState(!!gtmTag)
	const [state, setState] = useState<ICreateTag>({
		gtmAccount: null,
		gtmContainer: null,
		link: null,
		gtmWorkspace: null,
		oauth: 'PENDING'
	})

	const justAuthenticatedRef = useRef(false)

	const [error, setError] = useState<IStateError>({
		link: undefined,
		gtmAccount: undefined,
		gtmContainer: undefined,
		gtmWorkspace: undefined
	})

	const isGTMDisabled = useMemo(() => {
		return userAccess(user!.id) === 'READ' || demoMode || isGTMUpdated
	}, [user, demoMode, isGTMUpdated])

	const reset = useCallback(() => {
		clearAuthCode()
		setState((prevState) => {
			return { ...prevState, oauth: 'PENDING' }
		})
	}, [])

	const fetchGtmAccounts = async (id: string) => {
		if (!account || !asset || !id) return
		const data = (await getGtmAccounts(account.id, asset.id, id)) ?? []
		setGtmAccounts(data)
	}

	const fetchContainer = async () => {
		if (
			!account ||
			!asset ||
			!state.link ||
			!state.gtmAccount?.id ||
			isGTMDisabled
		)
			return
		const data =
			(await getGtmContainers(
				account!.id,
				asset!.id,
				state.gtmAccount.id,
				state.link.id
			)) ?? []
		setContainer(data)
	}

	const fetchWorkspace = async () => {
		if (!state.gtmContainer || isGTMDisabled) return
		const data =
			(await getGtmWorkspaces(
				account!.id,
				asset!.id,
				state.gtmAccount!.id,
				state.gtmContainer!.id,
				state.link!.id
			)) ?? []
		setWorkspaces(data)
	}

	const handleAccountChange = async (val: GtmAccount) => {
		if (!state.link) return
		setState({
			...state,
			gtmAccount: val,
			gtmContainer: null,
			gtmWorkspace: null
		})
		setError((prevState) => ({ ...prevState, gtmAccount: undefined }))
		setWorkspaces([])
	}

	const handleAccess = async (val: GoogleOAuthAccountLinkDTO) => {
		setState({
			...state,
			gtmAccount: null,
			gtmContainer: null,
			gtmWorkspace: null,
			link: val
		})
		fetchGtmAccounts(val.id)
		setIsGTMUpdated(false)
		setContainer([])
		setWorkspaces([])
	}

	const handleContainerChange = async (val: GtmContainer) => {
		if (!state.link) return
		setState({ ...state, gtmContainer: val, gtmWorkspace: null })
		setError((prevState) => ({ ...prevState, gtmContainer: undefined }))
	}

	const handleCreateTag = async () => {
		setError({
			link: !state.link ? 'Please select an Auth account' : undefined,
			gtmAccount: !state.gtmAccount ? 'Please select an account' : undefined,
			gtmContainer: !state.gtmContainer
				? 'Please select a container'
				: undefined,
			gtmWorkspace: !state.gtmWorkspace
				? 'Please select a workspace'
				: undefined
		})
		if (!state.gtmWorkspace || !state.link || !account?.id || !asset?.id) return

		// Track PostHog event for adding tracking code via GTM
		const isInSetup =
			account?.status === 'SETUP' || account?.setup?.step !== 'SETUP_COMPLETE'
		if (isInSetup) {
			trackPostHogEvent(
				PostHogEvent.ONBOARDING_COMPLETE_TC_STEP,
				PostHogPath.ONBOARDING,
				{
					server_side_tracking: false,
					tc_status: 'success', // Will be updated when verification completes
					method: 'google_tag_manager'
				}
			)
		}

		await createGtmTag(
			account.id,
			asset.id,
			state.gtmWorkspace.accountId,
			state.gtmWorkspace.containerId,
			state.gtmWorkspace.workspaceId,
			state.link.id
		)

		if (onComplete) {
			onComplete()
		}
	}

	useEffect(() => {
		if (state.oauth === 'WAITING') {
			const interval = setInterval(async () => {
				const code = getAuthCode()
				if (code && asset && account) {
					clearInterval(interval)
					clearAuthCode()
					await createGtmAuth(account.id, asset.id, code)
					justAuthenticatedRef.current = true
					setState((prevState) => ({
						...prevState,
						oauth: 'COMPLETED'
					}))
				}
			}, 1500)
		}
	}, [state.oauth])

	useEffect(() => {
		if (justAuthenticatedRef.current && gtmLinks.length > 0 && !state.link) {
			justAuthenticatedRef.current = false
			const newLink = gtmLinks[gtmLinks.length - 1]
			handleAccess(newLink)
		}
	}, [gtmLinks])

	useEffect(() => {
		if (state?.gtmAccount?.id) {
			fetchContainer()
		}
	}, [state.gtmAccount])

	useEffect(() => {
		if (state?.gtmContainer?.id) {
			fetchWorkspace()
		}
	}, [state.gtmContainer])

	useEffect(() => {
		if (gtmTag && gtmLinks) {
			let link = gtmLinks.find((l) => l.id === gtmTag.link_id)
			if (!link) {
				link = { name: 'Google Auth' } as GoogleOAuthAccountLinkDTO
			}
			setState({
				...state,
				gtmAccount: gtmTag.gtmAccount,
				gtmContainer: gtmTag.gtmContainer,
				gtmWorkspace: gtmTag.gtmWorkspace,
				link
			})
		}
	}, [gtmTag, gtmLinks])

	useEffect(() => {
		if (gtmTag) {
			setIsGTMUpdated(true)
		}
	}, [gtmTag])

	return (
		<div>
			<div className="flex flex-col gap-3">
				<div className={'flex flex-col'}>
					<p className="font-medium flex items-center gap-2 text-base-blue">
						Authorized with
					</p>
					<div className="flex items-center gap-4 w-full">
						<div className="flex-1 relative">
							<GListBox<GoogleOAuthAccountLinkDTO>
								options={gtmLinks}
								value={state.link as any}
								placeholder="Select credential"
								className="w-full"
								containerClass="w-full"
								onChange={(val) => handleAccess(val)}
								renderLabel={(val) => <span>{val?.name}</span>}
								disabled={isLoading('ASSET_SET_GTM_AUTH')}
								error={error.link}
							/>
						</div>
					</div>
					<div>
						<GButton
							variant="text"
							label={`${
								gtmLinks.length === 0
									? 'New credential'
									: 'Authorize with a different email'
							}`}
							disabled={demoMode || userAccess(user!.id) === 'READ'}
							color="buttonPrimary"
							iconPlacement="left"
							icon={RiAddLine}
							iconClassName="text-primary-500"
							labelClassName="text-base font-medium"
							className="h-[42px] border-none outline-0 bg-transparent hover:bg-transparent transition-all duration-200 shadow-none border-[var(--gtm-cred-border,#111553)] rounded-full p-0"
							onClick={() => {
								clearAuthCode()
								setState({ ...state, oauth: 'WAITING' })
								window.open(GoogleTagManagerAuthConfig.AUTH_URL, '_blank')
							}}
						/>
					</div>
				</div>
				<div className="flex flex-col gap-1">
					<p className="flex items-center gap-2 font-medium text-base">
						GTM Account
					</p>
					<GListBox<GtmAccount>
						className="w-full"
						containerClass="w-full"
						options={gtmAccounts}
						value={state.gtmAccount as any}
						placeholder="Select account"
						onChange={(val) => handleAccountChange(val)}
						renderLabel={(val) => <span>{val?.name}</span>}
						disabled={
							isLoading('ASSET_GET_GTM_ACCOUNTS') ||
							isLoading('ASSET_SET_GTM_AUTH') ||
							isGTMDisabled
						}
						error={error.gtmAccount}
					/>
				</div>
				<div className="flex flex-col gap-1">
					<p className="flex item-center gap-2 font-medium text-base">
						Container
					</p>
					<GListBox<GtmContainer>
						className="w-full"
						containerClass="w-full"
						options={gtmContainer}
						value={state.gtmContainer as any}
						placeholder="Select container"
						onChange={(val) => handleContainerChange(val)}
						renderLabel={(val) => <span>{val?.name}</span>}
						disabled={
							isLoading('ASSET_GET_GTM_CONTAINERS') ||
							isLoading('ASSET_SET_GTM_AUTH') ||
							isGTMDisabled
						}
						error={error.gtmContainer}
					/>
				</div>
				<div className="flex flex-col gap-1">
					<p className="flex items-center gap-2 font-medium text-base">
						Workspace
					</p>
					<GListBox<GtmWorkspace>
						className="w-full"
						containerClass="w-full"
						options={gtmWorkspaces}
						value={state.gtmWorkspace as any}
						placeholder="Select container"
						onChange={(val) => setState({ ...state, gtmWorkspace: val })}
						renderLabel={(val) => <span>{val?.name}</span>}
						disabled={
							isLoading('ASSET_GET_GTM_WORKSPACES') ||
							isLoading('ASSET_SET_GTM_AUTH') ||
							isGTMDisabled
						}
						error={error.gtmWorkspace}
					/>
				</div>
			</div>
			<div>
				{isGTMUpdated ? (
					<div className="flex flex-col gap-4 pt-6">
						<div className="flex items-center gap-2">
							<RiCheckLine className="w-6 h-6 text-green-500" />
							<p className="text-t-secondary text-base">
								Tracking code installed.
							</p>
						</div>
						{onComplete && (
							<GButton
								label="Go to Dashboard"
								color="yellow"
								labelClassName="font-medium text-base"
								className="h-[46px] w-full rounded-full !shadow-s-yellow"
								variant="contained"
								onClick={onComplete}
								size="md"
							/>
						)}
					</div>
				) : (
					<div className="py-6 flex flex-col gap-6 w-full items-center justify-center">
						<p className="font-normal text-t-gray-checkout">
							A new tag with ClickGuard's tracking code will be
							<br />
							created and published to your selected workspace.
						</p>
						<div className="flex w-full">
							<GTooltip
								content={
									userAccess(user!.id) === 'READ'
										? "Analysts don't have permission for this action"
										: undefined
								}
							>
								<GButton
									label="Install Tracking Code"
									color="yellow"
									labelClassName="font-medium text-base"
									className="h-[46px] w-full rounded-full disabled:!shadow-none !shadow-s-yellow"
									variant="contained"
									disabled={
										userAccess(user!.id) === 'READ' ||
										demoMode ||
										isLoading('ASSET_SET_GTM_TAGS')
									}
									onClick={handleCreateTag}
									loading={isLoading('ASSET_SET_GTM_TAGS')}
									size="md"
								/>
							</GTooltip>
						</div>
						<GTransition show={state.oauth === 'WAITING'}>
							<div className="flex flex-col items-center text-md">
								<span>Waiting for authorization...</span>
								<span
									className="text-red-700 cursor-pointer"
									onClick={() => reset()}
								>
									Cancel
								</span>
							</div>
						</GTransition>
					</div>
				)}
			</div>
		</div>
	)
}
